import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCol,
  IonFooter,
  IonGrid,
  IonImg,
  IonRow
} from '@ionic/react';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ActionIconButton from '@components/buttons/ActionIconButton';
import ClientAvatar from '@components/ClientAvatar';
import DateAndTimeStamp from '@components/dates/DateAndTimeStamp';
import styles from '@components/expert/ExpertMessage.module.css';
import ExpertAvatar from '@components/ExpertAvatar';
import ExpertRoleBoxes from '@components/ExpertRoleBoxes';
import FileResource from '@components/FileResource';
import TextWithIcon from '@components/text/TextWithIcon';
import TinyText from '@components/text/TinyText';
import useMyExpert from '@hooks/useMyExpert';
import useSanitizeHtml from '@hooks/useSanitizeHtml';
import { GetExpertUnreadMessagesQuery } from '@typing/Generated';
import { imageUrl } from '@utils/imageUtils';
import { messagePresentedName } from '@utils/modelUtils';
import { journeyRoute } from '@utils/routeUtils';

type Message = NonNullable<
  NonNullable<NonNullable<GetExpertUnreadMessagesQuery['expertUnreadMessages']['edges']>[number]>['node']
>;

type Props = {
  hideControls: boolean;
  journeyId: string;
  markJourneyMessagesRead: () => void;
  message: Message;
};

const ExpertMessage = ({ hideControls, journeyId, markJourneyMessagesRead, message }: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const sanitizedBody = useSanitizeHtml(message.body, journeyId, message);

  const avatar = useMemo(() => {
    if (message.fromGrayce) {
      return (
        <IonImg
          alt={intl.formatMessage({ id: 'dictionary.fromGrayce' })}
          className={styles.logo}
          src={imageUrl('logoIcon')}
        />
      );
    } else if (message.author.__typename === 'Expert') {
      if (message.author.photoUrl) {
        return <ExpertAvatar expert={message.author} size={50} />;
      } else {
        return (
          <div className={styles.scanning} data-test="scanning">
            <TextWithIcon icon="spinner" iconFirst text={intl.formatMessage({ id: 'dictionary.scanning' })} />
          </div>
        );
      }
    } else if (message.author.__typename === 'ClientDeletion') {
      return (
        <div className={styles.clientPhotoUrl}>
          <ClientAvatar
            clientId={null}
            color="primary500"
            firstName={intl.formatMessage({ id: 'models.clientDeletion.firstName' })}
            journeyId={null}
            lastName={intl.formatMessage({ id: 'models.clientDeletion.lastName' })}
            size={30}
          />
        </div>
      );
    } else {
      return (
        <div className={styles.clientPhotoUrl}>
          <ClientAvatar
            clientId={message.author.id}
            color="primary500"
            firstName={message.author.firstName}
            journeyId={journeyId}
            lastName={message.author.lastName}
            size={30}
          />
        </div>
      );
    }
  }, [intl, journeyId, message.author, message.fromGrayce]);

  return (
    <IonCard className={styles.card} data-test="message">
      <IonCardHeader>
        <IonCardTitle>
          <IonGrid className={styles.titleGrid}>
            <IonRow className="ion-justify-content-start">
              <IonCol size="auto">{avatar}</IonCol>
              <IonCol>
                {messagePresentedName(message, intl)}
                <div>
                  <TinyText>
                    <DateAndTimeStamp value={message.createdAt} />
                  </TinyText>
                </div>
              </IonCol>

              <>
                {message.chat.clients.map(client => (
                  <IonCol className={styles.avatar} key={client.id} size="auto">
                    <ClientAvatar
                      clientId={client.id}
                      color="primary700"
                      firstName={client.firstName}
                      journeyId={journeyId}
                      lastName={client.lastName}
                      size={30}
                    />
                  </IonCol>
                ))}
              </>

              {!hideControls && (
                <>
                  <IonCol size="auto">
                    <div className={styles.topSpacing}>
                      <ExpertRoleBoxes expertRoles={message.chat.journey.expertRoles} />
                    </div>
                  </IonCol>

                  <IonCol size="auto">
                    <div />
                    <div className={styles.alignIcons}>
                      <ActionIconButton
                        ariaLabelKey="accessibility.ariaLabels.openMessages"
                        hoverContent={<FormattedMessage id="pages.expert.dashboard.icons.commentEditIcon" />}
                        icon="commentEdit"
                        routerLink={journeyRoute({ journeyId, linkAfterJourney: '/messages', myExpert })}
                      />
                      <ActionIconButton
                        ariaLabelKey="accessibility.ariaLabels.markAsRead"
                        hoverContent={<FormattedMessage id="pages.expert.dashboard.markAsRead" />}
                        icon="envelopeOpen"
                        onClick={markJourneyMessagesRead}
                      />
                    </div>
                  </IonCol>
                </>
              )}
            </IonRow>
          </IonGrid>
        </IonCardTitle>
      </IonCardHeader>

      <IonCardContent className={styles.body}>{sanitizedBody}</IonCardContent>
      {message.fileResources.length > 0 && (
        <IonFooter>
          <IonGrid>
            {message.fileResources.map(fileResource => (
              <IonRow key={fileResource.id}>
                <IonCol className={styles.attachment}>
                  <FileResource fileResource={fileResource} />
                </IonCol>
              </IonRow>
            ))}
          </IonGrid>
        </IonFooter>
      )}
    </IonCard>
  );
};

export default ExpertMessage;
